import { createFileRoute, Link, useNavigate, useParams } from '@tanstack/react-router';
import {
  Anchor, Button, Group, Loader, NumberInput, Select, SimpleGrid, Stack, Switch, Text, TextInput, Textarea, rem,
} from '@mantine/core';
import { QueryErrorAlert } from '@/shared/components/QueryErrorAlert';
import {
  IconArrowLeft, IconBuilding, IconCircleCheck, IconCircleX, IconCoin, IconId, IconInfoCircle,
  IconMail, IconReceipt2, IconRosette, IconSitemap, IconTrash, IconWorld, IconPlugConnected, IconToggleLeft,
} from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from '@mantine/form';
import { modals } from '@mantine/modals';
import { useEffect, useMemo, useState } from 'react';
import { deleteOne, fetchAllPages, fetchList, fetchOne, updateOne } from '@/shared/api/jsonapi/client';
import { JsonApiHttpError } from '@/shared/api/jsonapi/errors';
import { notifySuccess, notifyError } from '@/shared/lib/notify';
import { useDirtyFormGuard } from '@/shared/hooks/useDirtyFormGuard';
import { useIsSuperAdmin } from '@/shared/hooks/useIsSuperAdmin';
import { MaeShell, MaePageHead } from '@/shared/desktop/MaeShell';
import { StatCard } from '@/shared/components/StatCard';
import { mw, MW_CARD_SHADOW } from '@/shared/mobile/tokens';
import type { ServiceItem } from '@/features/admin/serviceItems';
import type { Tenant } from '@/shared/types/entities';
import { useAuth } from '@/app/providers/AuthProvider';
import { ExternalSyncTenantTab } from '@/features/admin/ExternalSyncTenantTab';
import { ModulesSection } from '@/features/tenant-modules/TenantModulesPage';
import { fetchExternalSyncRegistry } from '@/features/admin/externalSync';
import { descendantsOf } from '@/features/tenants/groupTree';
import { isGroupRoot } from '@/features/tenants/groupRoot';
import { GROUP_POOL_DESCRIPTION, GROUP_POOL_LABEL } from '@/features/tenants/groupPool';
import {
  readRawOverride, serializeOverride, type ChooseDefault, type ChooseEntity,
} from '@/features/admin/externalSyncConfig';

export const Route = createFileRoute('/_app/admin/tenants/$tenantId')({ component: TenantDetailPage });

type RelationshipType = 'standalone' | 'holding_parent' | 'subsidiary' | 'affiliate' | 'joint_venture' | 'branch' | 'division';
type IndustryCodeSystem = 'ateco' | 'nace' | 'naics' | 'sic' | 'isic' | 'other';
type TabKey = 'anagrafica' | 'contatti' | 'fiscal' | 'billing' | 'holding' | 'sync' | 'modules' | 'plan';

const TABS: { key: TabKey; label: string; icon: typeof IconId }[] = [
  { key: 'anagrafica', label: 'Anagrafica', icon: IconId },
  { key: 'contatti', label: 'Contatti', icon: IconMail },
  { key: 'fiscal', label: 'Dati fiscali', icon: IconReceipt2 },
  // Separata dai dati fiscali di proposito: P.IVA e IBAN sono l'identità dell'azienda,
  // questi sono interruttori che cambiano come si comporta il sistema.
  { key: 'billing', label: 'Fatturazione & ordini', icon: IconCoin },
  { key: 'holding', label: 'Struttura societaria', icon: IconSitemap },
  /* Sta dopo la struttura societaria di proposito: la cascata della policy risale quella catena,
     quindi chi arriva qui ha appena visto da chi eredita. */
  { key: 'sync', label: 'Gestionale esterno', icon: IconPlugConnected },
  /* Gli interruttori di qualunque azienda, non solo di quelle di primo livello: li accende la
     piattaforma (`BE-RESPONSE-TO-FE-MODULI-v1` §7). La sezione si regola da sé su chi guarda. */
  { key: 'modules', label: 'Moduli', icon: IconToggleLeft },
  { key: 'plan', label: 'Piano & stato', icon: IconRosette },
];

/** §2.2 — ultimo anello della catena riga → ordine → azienda. Default `off`. */
const ORDER_HOURS_POLICY = [
  { value: 'off', label: 'Non controllare' },
  { value: 'warn', label: 'Avvisa chi sfora' },
  { value: 'block', label: 'Blocca lo sforamento' },
];

interface FormVals {
  name: string;
  organization: string;
  description: string;
  email: string;
  certifiedEmail: string;
  phoneNumber: string;
  webSite: string;
  vatNumber: string;
  taxId: string;
  eInvoicingCode: string;
  defaultIban: string;
  owner: string;
  commercial: string;
  shareCapital: number | null;
  shareCapitalCurrency: string;
  plan: string;
  enabled: boolean;
  tenantType: string;
  legalForm: string;
  industryCode: string;
  industryCodeSystem: IndustryCodeSystem | '';
  ccnl: string;
  defaultCurrency: string;
  defaultLang: string;
  timezone: string;
  country: string;
  fiscalYearStartMonth: number | null;
  fiscalYearStartDay: number | null;
  /**
   * ORDERS-LEGACY-PARITY §6.3 — articolo di riserva della fatturazione: **id scalare**, non
   * una relationship. Il BE l'ha esposto così di proposito, per non allargare l'albero degli
   * include di `tenants` (il limite dei 4096 elementi della select list è già stato sforato
   * una volta). Vuoto = ultimo anello assente, e una riga che arriva fin qui non si fattura.
   */
  fallbackServiceItemId: string | null;
  /** §2.2 — `off` | `warn` | `block`. */
  orderHoursPolicy: string;
  /** §4.3 — le voci create dall'import nascono da confermare. */
  requireSubActivityApproval: boolean;
  // Holding
  relationshipType: RelationshipType | '';
  parentTenantId: string | null;
  parentShare: number | null;
  isHoldingOnly: boolean;
  enableGroupPool: boolean;
  // Gestionale esterno
  /** `'eredita'` = attributo `null`, cioè «lo decide la capogruppo». */
  externalSyncPolicy: ChooseDefault;
  /**
   * `entityType` → scelta, **non** serializzato: la stringa JSON la compone il salvataggio.
   * Tenerlo come mappa qui è ciò che rende il `dirty` del form affidabile — su una stringa
   * dipenderebbe dall'ordine delle chiavi.
   */
  externalSyncChoices: Record<string, ChooseEntity>;
}

const EMPTY: FormVals = {
  name: '', organization: '', description: '', email: '', certifiedEmail: '', phoneNumber: '', webSite: '',
  vatNumber: '', taxId: '', eInvoicingCode: '', defaultIban: '',
  owner: '', commercial: '', shareCapital: null, shareCapitalCurrency: 'EUR',
  plan: 'free', enabled: true,
  tenantType: '', legalForm: '', industryCode: '', industryCodeSystem: 'ateco', ccnl: '',
  defaultCurrency: 'EUR', defaultLang: 'it', timezone: 'Europe/Rome', country: 'IT',
  fiscalYearStartMonth: 1, fiscalYearStartDay: 1,
  fallbackServiceItemId: null, orderHoursPolicy: 'off', requireSubActivityApproval: false,
  relationshipType: 'standalone', parentTenantId: null, parentShare: null, isHoldingOnly: false,
  enableGroupPool: false,
  externalSyncPolicy: 'eredita', externalSyncChoices: {},
};

const RELATIONSHIP_TYPES: { value: RelationshipType; label: string }[] = [
  { value: 'standalone', label: 'Standalone' },
  { value: 'holding_parent', label: 'Capogruppo (holding)' },
  { value: 'subsidiary', label: 'Controllata' },
  { value: 'affiliate', label: 'Collegata' },
  { value: 'joint_venture', label: 'Joint venture' },
  { value: 'branch', label: 'Filiale' },
  { value: 'division', label: 'Divisione' },
];

const INDUSTRY_SYSTEMS: { value: IndustryCodeSystem; label: string }[] = [
  { value: 'ateco', label: 'ATECO (IT)' },
  { value: 'nace', label: 'NACE (EU)' },
  { value: 'naics', label: 'NAICS (US/CA/MX)' },
  { value: 'sic', label: 'SIC (US)' },
  { value: 'isic', label: 'ISIC (UN)' },
  { value: 'other', label: 'Altro' },
];

function TenantDetailPage() {
  const { tenantId } = useParams({ from: '/_app/admin/tenants/$tenantId' });
  const navigate = useNavigate();
  const qc = useQueryClient();
  const isSuperAdmin = useIsSuperAdmin();

  const tenantQ = useQuery({
    queryKey: ['tenant', tenantId],
    enabled: Boolean(tenantId),
    queryFn: () => fetchOne<Tenant>('tenants', tenantId),
  });
  const tenant = tenantQ.data;

  const parentsQ = useQuery({
    queryKey: ['tenants-parents-pick', tenantId],
    enabled: Boolean(tenantId),
    queryFn: () => fetchAllPages<Tenant>('tenants', { include: ['parentTenant'] }, 200),
  });

  /* Il listino per l'articolo di riserva. `fallbackServiceItemId` è un **id scalare** e non una
     relationship — scelta del BE per non allargare l'albero degli include di `tenants` — quindi
     l'elenco va letto a parte per poter mostrare un nome invece di un GUID. */
  const serviceItemsQ = useQuery({
    queryKey: ['service-items-pick'],
    staleTime: 5 * 60_000,
    queryFn: () => fetchList<ServiceItem>('service-items', { sort: ['name'], page: { size: 100 } }),
  });

  /* Il registro serve a due cose: elencare le entità configurabili, e sapere **quali chiavi
     sono ancora buone**. La seconda è quella che conta al salvataggio: una chiave che il
     registro non conosce prende 422, quindi finché questa query non risponde le eccezioni non
     si toccano. `retry: false` come nell'hook: una `queryKey` sola per tutta l'app. */
  const registerQ = useQuery({
    queryKey: ['external-sync-registry'],
    staleTime: 30 * 60_000,
    retry: false,
    queryFn: fetchExternalSyncRegistry,
  });
  const knownEntities = useMemo(
    () => [...(registerQ.data?.perResource.values() ?? [])].map((v) => v.entityType),
    [registerQ.data],
  );

  const { decoded } = useAuth();
  /* Il registro risolve la cascata per **il tenant di chi chiede**, cioè quello del JWT: su
     un'azienda diversa da questa la colonna «in vigore» non parlerebbe di lei. */
  const activeTenantId = decoded?.tenantId ?? null;

  /* Si guarda il valore **salvato**, non quello del form: il form porta già la mappa ripulita,
     quindi da lì non si distinguerebbe «non c'era niente» da «non era leggibile». */
  const syncUnreadable = readRawOverride(
    (tenant as unknown as Record<string, unknown> | null)?.externalSyncPolicyOverrides as string | null,
  ).unreadable;

  const [tab, setTab] = useState<TabKey>('anagrafica');

  const form = useForm<FormVals>({ mode: 'controlled', initialValues: EMPTY });
  useEffect(() => {
    if (!tenant) return;
    const t = tenant as unknown as Record<string, unknown>;
    const str = (k: string) => (typeof t[k] === 'string' ? (t[k] as string) : '');
    const bool = (k: string) => Boolean(t[k]);
    const num = (k: string) => (typeof t[k] === 'number' ? (t[k] as number) : null);
    const parentRel = (t.parentTenant as { id?: string } | null | undefined);
    const vals: FormVals = {
      name: str('name'), organization: str('organization'), description: str('description'),
      email: str('email'), certifiedEmail: str('certifiedEmail'), phoneNumber: str('phoneNumber'), webSite: str('webSite'),
      vatNumber: str('vatNumber'), taxId: str('taxId'), eInvoicingCode: str('eInvoicingCode'), defaultIban: str('defaultIban'),
      owner: str('owner'), commercial: str('commercial'),
      shareCapital: num('shareCapital'), shareCapitalCurrency: str('shareCapitalCurrency') || 'EUR',
      plan: str('plan') || 'free', enabled: bool('enabled'),
      tenantType: str('tenantType'), legalForm: str('legalForm'),
      industryCode: str('industryCode'), industryCodeSystem: (str('industryCodeSystem') || 'ateco') as IndustryCodeSystem,
      ccnl: str('ccnl'),
      defaultCurrency: str('defaultCurrency') || 'EUR',
      defaultLang: str('defaultLang') || 'it',
      timezone: str('timezone') || 'Europe/Rome',
      country: str('country') || 'IT',
      fiscalYearStartMonth: num('fiscalYearStartMonth') ?? 1,
      fiscalYearStartDay: num('fiscalYearStartDay') ?? 1,
      fallbackServiceItemId: str('fallbackServiceItemId') || null,
      orderHoursPolicy: str('orderHoursPolicy') || 'off',
      requireSubActivityApproval: bool('requireSubActivityApproval'),
      relationshipType: (str('relationshipType') || 'standalone') as RelationshipType,
      parentTenantId: parentRel?.id ?? null,
      parentShare: num('parentShare'),
      isHoldingOnly: bool('isHoldingOnly'),
      enableGroupPool: bool('enableGroupPool'),
      /* `null` non è «spento»: è «eredita dalla capogruppo». Tenerli distinti è il punto. */
      externalSyncPolicy: (str('externalSyncPolicy') || 'eredita') as ChooseDefault,
      /* Si leggono **tutte** le chiavi, anche quelle che il registro non conosce: separarle qui
         significherebbe cancellarle quando la GET del registro cade. La cernita è al render. */
      externalSyncChoices: readRawOverride(str('externalSyncPolicyOverrides') || null).map,
    };
    form.setValues(vals);
    // Allinea lo stato "pulito" ai dati appena caricati: il dirty-guard deve
    // scattare solo su modifiche reali dell'utente, non sul populate iniziale.
    form.resetDirty(vals);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant?.id]);

  const save = useMutation({
    mutationFn: (v: FormVals) => {
      const {
        parentTenantId, relationshipType, industryCodeSystem,
        externalSyncPolicy, externalSyncChoices, ...rest
      } = v;
      const attributes: Record<string, unknown> = {
        ...rest,
        relationshipType: relationshipType || null,
        industryCodeSystem: industryCodeSystem || null,
        /* `'eredita'` è l'assenza di decisione, e sul wire si scrive `null`. */
        externalSyncPolicy: externalSyncPolicy === 'eredita' ? null : externalSyncPolicy,
      };

      /*
       * ⚠️ Le eccezioni si mandano **solo se il registro ha risposto**, e l'omissione è la cosa
       * giusta: `serializzaOverride` scarta le chiavi che il registro non conosce — che al
       * salvataggio darebbero 422 — e con un registro vuoto le scarterebbe tutte. Una PATCH
       * JSON:API che non nomina un attributo lo lascia com'è, quindi tacere qui **conserva** la
       * configurazione, mentre mandare `null` la cancellerebbe per un guasto di rete.
       *
       * La scheda lo dice invece di lasciarlo intuire: senza registro il pannello mostra il suo
       * stato d'errore, e le eccezioni non sono modificabili.
       */
      if (knownEntities.length > 0) {
        attributes.externalSyncPolicyOverrides = serializeOverride(externalSyncChoices, knownEntities);
      }
      const relationships: Record<string, { data: { type: string; id: string } | null }> = {};
      if (parentTenantId) {
        relationships.parentTenant = { data: { type: 'tenants', id: parentTenantId } };
      } else {
        relationships.parentTenant = { data: null };
      }
      return updateOne<Tenant>('tenants', { id: tenantId, attributes, relationships });
    },
    onSuccess: () => {
      notifySuccess('Azienda aggiornata');
      form.resetDirty();
      qc.invalidateQueries({ queryKey: ['tenant', tenantId] });
      qc.invalidateQueries({ queryKey: ['jsonapi', 'tenants'] });
    },
    // BE v1.9: la validazione holding/internazionale risponde 422 con source.pointer
    // sul campo incoerente — la mostriamo puntuale sul form, non solo a toast.
    onError: (err) => {
      if (err instanceof JsonApiHttpError && err.isValidationError()) {
        const fieldErrors = err.toFieldErrors();
        if (Object.keys(fieldErrors).length > 0) {
          form.setErrors(fieldErrors);
          return;
        }
      }
      notifyError(err);
    },
  });

  const remove = useMutation({
    mutationFn: () => deleteOne('tenants', tenantId),
    onSuccess: () => {
      notifySuccess('Azienda eliminata');
      qc.invalidateQueries({ queryKey: ['jsonapi', 'tenants'] });
    },
    /* Il motivo lo dice il sistema (task 1739): un ramo con un'azienda protetta è un 409 coi nomi. */
    onError: (err) => notifyError(err, 'Azienda non eliminata'),
  });

  // Protegge il form da uscite accidentali con modifiche non salvate.
  // Disattivato dopo l'eliminazione: il redirect post-delete non va bloccato.
  useDirtyFormGuard(form.isDirty() && !remove.isSuccess);

  // Navigazione post-delete in effect (non in onSuccess) così il blocker
  // viene ri-registrato con il guard disattivato prima del navigate.
  useEffect(() => {
    if (remove.isSuccess) void navigate({ to: '/admin/tenants' });
  }, [remove.isSuccess, navigate]);

  if (tenantQ.isError) return <QueryErrorAlert error={tenantQ.error} title="Impossibile leggere l’azienda" onRetry={() => void tenantQ.refetch()} />;
  if (tenantQ.isLoading) {
    return <MaeShell><Group justify="center" py={60}><Loader /></Group></MaeShell>;
  }
  if (!tenant) {
    return (
      <MaeShell>
        <MaePageHead icon={<IconBuilding size={22} />} title="Azienda non trovata" sub="L’azienda richiesta non esiste o non è accessibile." />
        <Anchor component={Link} to="/admin/tenants" size="sm" style={{ color: mw.accentText, fontWeight: 600 }}>
          <Group gap={4}><IconArrowLeft size={14} /> Torna all’elenco delle aziende</Group>
        </Anchor>
      </MaeShell>
    );
  }

  /*
   * ⚠️ FUORI DALLE SCELTE ANCHE LE PROPRIE CONTROLLATE, non solo sé stessa.
   *
   * Qui si escludeva la sola azienda aperta. Bastava quindi scegliere come capogruppo una
   * propria figlia — o una nipote — per chiudere un anello: A controlla B, e B controlla A.
   * Il salvataggio riesce, perché il server non guarda il ciclo; a rompersi è **dopo**, e
   * altrove: `raggruppaAziende` non sa più disegnare quel ramo e le due finiscono fra le
   * «aziende fuori dalla struttura», cioè sparite dall'albero senza che nessuno abbia
   * cancellato niente.
   *
   * La scheda `/organization/company` la regola ce l'aveva già (`discendentiDi`); questa no,
   * ed è lo stesso campo sulla stessa riga del database. Meglio non offrire la scelta che
   * scoprirla dopo la scrittura.
   */
  const forbidden = descendantsOf(
    (parentsQ.data ?? []) as unknown as Parameters<typeof descendantsOf>[0],
    tenantId,
  );
  const parentOptions = (parentsQ.data ?? [])
    .filter((p) => p.id !== tenantId && !forbidden.has(p.id))
    .map((p) => ({ value: p.id, label: (p.name as string) ?? p.id }));

  const isSubsidiary = form.values.relationshipType === 'subsidiary' || form.values.relationshipType === 'affiliate' || form.values.relationshipType === 'branch' || form.values.relationshipType === 'division';

  const planLabel = form.values.plan || 'free';
  const isEnabled = form.values.enabled;
  const isSystem = Boolean((tenant as unknown as { isSystemTenant?: boolean }).isSystemTenant);
  const childrenCount = (parentsQ.data ?? []).filter(
    (p) => (p as unknown as { parentTenant?: { id?: string } }).parentTenant?.id === tenantId,
  ).length;

  const cardStyle = {
    border: `1px solid ${mw.line}`, borderRadius: 16, background: mw.surface,
    boxShadow: MW_CARD_SHADOW, padding: 20,
  };
  const chip = (label: string, bg: string, color: string) => (
    <span style={{ fontSize: 'var(--fs-compact)', fontWeight: 500, padding: '4px 10px', borderRadius: 999, background: bg, color, textTransform: 'uppercase', letterSpacing: '.08em' }}>{label}</span>
  );

  return (
    <MaeShell>
      <Anchor component={Link} to="/admin/tenants" size="sm" style={{ display: 'inline-block', marginBottom: 10, color: mw.accentText, fontWeight: 600 }}>
        <Group gap={4}><IconArrowLeft size={14} /> Torna all’elenco delle aziende</Group>
      </Anchor>

      <MaePageHead
        icon={<IconBuilding size={22} />}
        title={tenant.name ?? tenantId}
        sub={form.values.organization || `Piano ${planLabel} · ${isEnabled ? 'attivo' : 'disattivato'}`}
        tone="bordeaux"
        actions={isSuperAdmin ? (
          <>
            <Button component={Link} to={`/admin/tenants/${tenantId}/subscription`} variant="light" color="dark" leftSection={<IconRosette size={16} />}>
              Sottoscrizione & licenze
            </Button>
            <Button
              variant="light"
              color="red"
              leftSection={<IconTrash size={16} />}
              loading={remove.isPending}
              onClick={() => {
                modals.openConfirmModal({
                  title: 'Elimina azienda',
                  children: (
                    <Text size="sm">
                      Eliminare l’azienda &quot;{tenant.name}&quot;? Vengono rimossi i suoi dati e <b>tutte le aziende sotto di lei</b>, a ogni livello. Se una di loro è protetta dalla cancellazione non si elimina niente.
                    </Text>
                  ),
                  labels: { confirm: 'Elimina', cancel: 'Annulla' },
                  confirmProps: { color: 'red' },
                  onConfirm: () => remove.mutate(),
                });
              }}
            >
              Elimina
            </Button>
          </>
        ) : undefined}
      />

      <Group gap={8} mb={16} wrap="wrap">
        {chip(planLabel, mw.brandTint, mw.brandText)}
        {chip(isEnabled ? 'Attivo' : 'Disattivato', isEnabled ? mw.successBg : mw.surface2, isEnabled ? mw.success : mw.ink500)}
        {form.values.isHoldingOnly && chip('Holding only', mw.accentTint, mw.accentText)}
        {isSystem && chip('System', mw.warningBg, mw.warning)}
      </Group>

      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing={14} mb={18}>
        <StatCard label="Piano" value={planLabel.toUpperCase()} icon={IconRosette} color="brand" />
        <StatCard label="Stato" value={isEnabled ? 'Attivo' : 'Disattivato'} icon={isEnabled ? IconCircleCheck : IconCircleX} color={isEnabled ? 'green' : 'gray'} />
        <StatCard label="Società controllate" value={childrenCount} icon={IconSitemap} color="blue" loading={parentsQ.isLoading} />
        <StatCard label="Paese" value={form.values.country || '—'} icon={IconWorld} color="gray" />
      </SimpleGrid>

      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', border: `1px solid ${mw.line}`, background: mw.accentTint, borderRadius: 14, padding: '12px 16px', marginBottom: 16 }}>
        <IconInfoCircle size={18} style={{ color: mw.accentText, flexShrink: 0, marginTop: 1 }} />
        <Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink500, lineHeight: 1.5 }}>
          Sede legale, sede operativa e sede di fatturazione si gestiscono ora da <Anchor component={Link} to="/workforce/sites" style={{ color: mw.accentText, fontWeight: 600 }}>Sedi aziendali</Anchor> usando i flag <code>isRegisteredOffice</code>, <code>isAdministrativeOffice</code>, <code>isMainOffice</code>, <code>isBillingAddress</code> (massimo una sede per flag).
        </Text>
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', padding: 6, background: mw.surface, border: `1px solid ${mw.line}`, borderRadius: 14, boxShadow: MW_CARD_SHADOW, marginBottom: 16 }}>
        {TABS.map((tb) => {
          const active = tb.key === tab;
          const Icon = tb.icon;
          return (
            <button
              key={tb.key}
              type="button"
              className="mw-pressable"
              onClick={() => setTab(tb.key)}
              style={{ display: 'flex', alignItems: 'center', gap: 8, height: rem(38), padding: '0 16px', borderRadius: 10, cursor: 'pointer', border: 0, fontFamily: 'inherit', fontSize: 'var(--fs-body)', fontWeight: active ? 700 : 600, background: active ? mw.brandFill : 'transparent', color: active ? 'var(--on-brand)' : mw.ink500 }}
            >
              <Icon size={16} /> {tb.label}
            </button>
          );
        })}
      </div>

      <form onSubmit={form.onSubmit((v) => save.mutate(v))}>
        <div style={{ display: tab === 'anagrafica' ? 'block' : 'none' }}>
          <div style={cardStyle}>
              <Stack>
                <Group grow>
                  <TextInput label="Nome" required {...form.getInputProps('name')} />
                  <TextInput label="Organizzazione" {...form.getInputProps('organization')} />
                </Group>
                <Textarea label="Descrizione" autosize minRows={2} {...form.getInputProps('description')} />
                <Group grow>
                  <Select
                    label="Tipo"
                    data={[{ value: '', label: '—' }, { value: 'HOLDING', label: 'Holding' }, { value: 'COMPANY', label: 'Company' }]}
                    {...form.getInputProps('tenantType')}
                  />
                  <TextInput label="Forma giuridica" placeholder="es. SRL, SPA" {...form.getInputProps('legalForm')} />
                </Group>
                <Group grow>
                  <Select
                    label="Sistema codice settore"
                    data={INDUSTRY_SYSTEMS}
                    {...form.getInputProps('industryCodeSystem')}
                  />
                  <TextInput label="Codice settore" placeholder="es. 62.01.00 (ATECO)" {...form.getInputProps('industryCode')} />
                </Group>
                <TextInput label="CCNL applicato" {...form.getInputProps('ccnl')} />
              </Stack>
          </div>
        </div>

        <div style={{ display: tab === 'contatti' ? 'block' : 'none' }}>
          <div style={cardStyle}>
              <Stack>
                <Group grow>
                  <TextInput label="Email" {...form.getInputProps('email')} />
                  <TextInput label="PEC" {...form.getInputProps('certifiedEmail')} />
                </Group>
                <Group grow>
                  <TextInput label="Telefono" {...form.getInputProps('phoneNumber')} />
                  <TextInput label="Sito web" {...form.getInputProps('webSite')} />
                </Group>
                <Group grow>
                  <TextInput label="Owner" {...form.getInputProps('owner')} />
                  <TextInput label="Referente commerciale" {...form.getInputProps('commercial')} />
                </Group>
              </Stack>
          </div>
        </div>

        <div style={{ display: tab === 'fiscal' ? 'block' : 'none' }}>
          <div style={cardStyle}>
              <Stack>
                <Group grow>
                  <TextInput label="P.IVA" {...form.getInputProps('vatNumber')} />
                  <TextInput label="Codice fiscale" {...form.getInputProps('taxId')} />
                </Group>
                <Group grow>
                  <TextInput label="Codice SDI / e-Invoicing" {...form.getInputProps('eInvoicingCode')} />
                  <TextInput label="IBAN di default" {...form.getInputProps('defaultIban')} />
                </Group>
                <Group grow>
                  <NumberInput label="Capitale sociale" decimalScale={2} {...form.getInputProps('shareCapital')} />
                  <Select
                    label="Valuta capitale"
                    data={['EUR', 'USD', 'GBP', 'CHF'].map((c) => ({ value: c, label: c }))}
                    {...form.getInputProps('shareCapitalCurrency')}
                  />
                </Group>
                <Group grow>
                  <NumberInput
                    label="Mese inizio esercizio fiscale"
                    description="1 = gennaio"
                    min={1}
                    max={12}
                    {...form.getInputProps('fiscalYearStartMonth')}
                  />
                  <NumberInput
                    label="Giorno inizio esercizio fiscale"
                    min={1}
                    max={31}
                    {...form.getInputProps('fiscalYearStartDay')}
                  />
                </Group>
              </Stack>
          </div>
        </div>

        {/* ── Fatturazione & ordini ─────────────────────────────────────────
            Tre interruttori che il BE ha messo su `tenants` nello stesso batch
            (ORDERS-LEGACY-PARITY §2.2, §4.3, §6.3) e che finora non erano configurabili da
            nessuna parte: l'articolo di riserva non aveva nemmeno `[Attr]`, quindi esisteva
            a DB, funzionava lato server ed era invisibile. */}
        <div style={{ display: tab === 'billing' ? 'block' : 'none' }}>
          <div style={cardStyle}>
            <Stack>
              <Select
                label="Articolo di riserva per la fatturazione"
                description="L'ultimo anello della catena: articolo della voce → articolo della riga d'ordine → regola di fatturazione → questo. Se resta vuoto e nient'altro ha risolto, la riga non si fattura."
                placeholder={serviceItemsQ.isLoading ? 'Caricamento articoli…' : 'Nessuno'}
                data={(serviceItemsQ.data?.data ?? [])
                  .filter((s) => s.isActive !== false)
                  .map((s) => ({ value: s.id, label: [s.code, s.name].filter(Boolean).join(' · ') || s.id }))}
                value={form.values.fallbackServiceItemId}
                onChange={(v) => form.setFieldValue('fallbackServiceItemId', v)}
                searchable
                clearable
                nothingFoundMessage="Nessun articolo a listino"
                error={serviceItemsQ.isError ? 'Listino non leggibile: riprova, o imposta l\'articolo più tardi.' : undefined}
              />
              {!form.values.fallbackServiceItemId && (
                <Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink500, lineHeight: 1.5 }}>
                  Senza articolo di riserva, una riga che arriva in fondo alla catena viene
                  rifiutata invece di essere
                  fatturata su un articolo generico. È una scelta legittima — significa
                  «voglio accorgermene» — ma va fatta sapendo cosa comporta.
                </Text>
              )}

              <Select
                label="Sforamento del monte ore d'ordine"
                description="Cosa fare quando si rendicontano più ore di quelle vendute sulla riga d'ordine. Vale per tutta l'azienda: la singola riga o il singolo ordine possono avere una politica propria, e quella vince."
                data={ORDER_HOURS_POLICY}
                value={form.values.orderHoursPolicy}
                onChange={(v) => form.setFieldValue('orderHoursPolicy', v ?? 'off')}
                allowDeselect={false}
              />

              <Switch
                label="Le voci di attività dall'import nascono da confermare"
                description="Con l'interruttore acceso, una voce creata dall'import di un ordine non è subito rendicontabile: qualcuno deve confermarla. Spento, è utilizzabile appena arriva."
                checked={form.values.requireSubActivityApproval}
                onChange={(e) => form.setFieldValue('requireSubActivityApproval', e.currentTarget.checked)}
              />
            </Stack>
          </div>
        </div>

        <div style={{ display: tab === 'holding' ? 'block' : 'none' }}>
          <div style={cardStyle}>
              <Stack>
                <Group grow>
                  <Select
                    label="Tipo di relazione"
                    data={RELATIONSHIP_TYPES}
                    {...form.getInputProps('relationshipType')}
                  />
                  <Switch
                    label="Solo holding (no operatività)"
                    description="L’azienda esiste per il consolidamento e non ha utenti operativi"
                    mt={24}
                    {...form.getInputProps('isHoldingOnly', { type: 'checkbox' })}
                  />
                </Group>
                <Group grow>
                  <Select
                    label="Capogruppo"
                    placeholder={isSubsidiary ? 'Seleziona capogruppo' : 'Standalone — nessuna capogruppo'}
                    data={parentOptions}
                    clearable
                    searchable
                    disabled={!isSubsidiary}
                    {...form.getInputProps('parentTenantId')}
                  />
                  <NumberInput
                    label="Quota di partecipazione (%)"
                    description="Range 0.01–100"
                    decimalScale={2}
                    min={0.01}
                    max={100}
                    disabled={!form.values.parentTenantId}
                    {...form.getInputProps('parentShare')}
                  />
                </Group>
                {/*
                  ⚠️ **Nome e descrizione arrivano da `groupPool.ts`, e prima no.**

                  Fino al 03-10-2026 questo interruttore si chiamava «Pool risorse di gruppo» e
                  parlava di staffing dei dipendenti, mentre lo stesso campo sulla pagina Holding
                  si chiamava «Visibilità di gruppo attiva» e parlava di configurazioni condivise.
                  Il flag fa tutte e due le cose: ognuna delle due descrizioni era vera a metà, e
                  chi le vedeva entrambe concludeva che fossero due interruttori diversi.

                  Che si scriva da due posti resta voluto — questo è l'editor di piattaforma, dove
                  ci sono i campi grezzi di qualunque azienda, come per quota e tipo di rapporto.
                  Quel che non deve succedere è che i due lo **chiamino** in due modi.
                */}
                <Switch
                  label={GROUP_POOL_LABEL}
                  description={`${GROUP_POOL_DESCRIPTION} Si imposta sulla capogruppo: acceso su una figlia non apre niente.`}
                  {...form.getInputProps('enableGroupPool', { type: 'checkbox' })}
                />
              </Stack>
          </div>
        </div>

        {/* ── Gestionale esterno ────────────────────────────────────────────
            La decisione che il BE risolveva e che il frontend non sapeva esprimere: fino al
            06-09-2026 `externalSyncPolicy` e `externalSyncPolicyOverrides` comparivano solo in
            `api.gen.ts`. La lettura c'era da settembre — i campi si spegnevano — ma «per ciascun
            tenant si sceglie sola lettura o misto» non aveva un posto dove essere detto. */}
        <div style={{ display: tab === 'sync' ? 'block' : 'none' }}>
          <div style={cardStyle}>
            <ExternalSyncTenantTab
              tenantId={tenantId}
              activeTenantId={activeTenantId}
              policy={form.values.externalSyncPolicy}
              onPolicy={(v) => form.setFieldValue('externalSyncPolicy', v)}
              choices={form.values.externalSyncChoices}
              onChoices={(v) => form.setFieldValue('externalSyncChoices', v)}
              unreadable={syncUnreadable}
              /* La regola, e il perché la piattaforma non conta, in `features/tenants/groupRoot.ts`. */
              isGroupRoot={isGroupRoot(form.values.parentTenantId)}
            />
          </div>
        </div>

        {/* Montata solo sulla scheda aperta: legge per un'azienda precisa, e non serve leggerla
            finché nessuno guarda i moduli. La restrizione di gruppo ha senso solo se c'è una
            capogruppo; senza, un modulo si spegne con l'interruttore. */}
        {tab === 'modules' && (
          <div style={cardStyle}>
            <ModulesSection tenantId={tenantId} groupRestriction={Boolean(form.values.parentTenantId)} />
          </div>
        )}

        <div style={{ display: tab === 'plan' ? 'block' : 'none' }}>
          <div style={cardStyle}>
              <Stack>
                <Group grow>
                  <Select
                    label="Piano"
                    data={[
                      { value: 'free', label: 'Free' },
                      { value: 'starter', label: 'Starter' },
                      { value: 'basic', label: 'Basic' },
                      { value: 'pro', label: 'Pro' },
                      { value: 'enterprise', label: 'Enterprise' },
                    ]}
                    {...form.getInputProps('plan')}
                  />
                  <Switch label="Azienda attiva" {...form.getInputProps('enabled', { type: 'checkbox' })} />
                </Group>
                <Group grow>
                  <TextInput
                    label="Paese (ISO 3166-1 alpha-2)"
                    placeholder="IT"
                    maxLength={2}
                    {...form.getInputProps('country')}
                  />
                  <Select
                    label="Valuta di default (ISO 4217)"
                    data={['EUR', 'USD', 'GBP', 'CHF'].map((c) => ({ value: c, label: c }))}
                    {...form.getInputProps('defaultCurrency')}
                  />
                </Group>
                <Group grow>
                  <Select
                    label="Lingua di default (ISO)"
                    data={[{ value: 'it', label: 'Italiano' }, { value: 'en', label: 'English' }, { value: 'fr', label: 'Français' }, { value: 'de', label: 'Deutsch' }, { value: 'es', label: 'Español' }]}
                    {...form.getInputProps('defaultLang')}
                  />
                  <TextInput label="Timezone (IANA)" placeholder="Europe/Rome" {...form.getInputProps('timezone')} />
                </Group>
              </Stack>
          </div>
        </div>

        <Group justify="flex-end" mt="lg">
          <Button variant="subtle" onClick={() => void navigate({ to: '/admin/tenants' })}>
            Annulla
          </Button>
          <Button type="submit" loading={save.isPending} style={{ background: mw.brandFill }}>Salva</Button>
        </Group>
      </form>
    </MaeShell>
  );
}
